// Scrollable conversation (replies + attachments) of an email sent from Case Tempo, read from the sender's
// Gmail / Outlook. Renders `fallback` when the sender did not turn on reading email conversations.
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import request from 'axios';
import { toastr } from 'react-redux-toastr';
import Button from './Button';
import ConfirmDialog from './ConfirmDialog';
import { Row, Col } from 'react-bootstrap';
import { getClientAuthHeader } from '../../../client/token';

const formatSize = size => {
    if (!size && size !== 0) {
        return '';
    }

    return size < 1024 * 1024 ? `${Math.max(1, Math.round(size / 1024))} KB` : `${(size / 1024 / 1024).toFixed(1)} MB`;
};

const fileExtension = name => ((name || '').split('.').pop() || '').toLowerCase().replace(/[^a-z0-9]/g, '');

const badgeKind = ext => {
    if (ext === 'pdf') {
        return 'pdf';
    }

    if (['doc', 'docx', 'rtf', 'odt', 'txt'].includes(ext)) {
        return 'doc';
    }

    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'heic', 'tif', 'tiff'].includes(ext) ? 'img' : 'other';
};

const IMAGE_TYPES = {jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp', bmp: 'image/bmp'};

// Only types the browser shows without running anything (no html / svg).
const previewKind = ext => (ext === 'pdf' ? 'pdf' : (IMAGE_TYPES[ext] ? 'image' : undefined));

const displayName = from => ((from || '').replace(/<.*>/, '').replace(/"/g, '').trim()) || from || '';

const initials = from => displayName(from).split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

// Messages written by the user who sent the email from Case Tempo get an accent.
const isSender = (from, sender) => !!sender && (from || '').toLowerCase().includes(sender.toLowerCase());

// Sandboxed (no scripts) and with links opening in a new tab.
const bodyDocument = html => `<base target="_blank"><style>body{font-family:sans-serif;font-size:14px;margin:0 14px 8px 58px;color:#16202e;word-wrap:break-word}</style>${html}`;

class EmailConversation extends PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            data: undefined,
            error: undefined,
            busy: {},
            saved: {},
            permissionDialogOpen: false,
            showQuoted: {},
            saveAllItems: undefined,
            savingAll: false,
            reconnect: false,
            enabling: false,
        };
    }

    componentDidMount() {
        this.load();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.emailId !== this.props.emailId || prevProps.caseId !== this.props.caseId) {
            this.load();
        }
    }

    url(suffix) {
        const { caseId, emailId } = this.props;
        return `/api/v1/case/${encodeURIComponent(caseId)}/email/${encodeURIComponent(emailId)}/${suffix}`;
    }

    load() {
        const emailId = this.props.emailId;
        this.setState({loading: true, data: undefined, error: undefined});
        request.get(this.url('conversation'), getClientAuthHeader())
            .then(result => {
                if (emailId === this.props.emailId) {
                    this.setState({loading: false, data: result.data});
                    if (this.props.onAttachmentCount) {
                        this.props.onAttachmentCount(((result.data && result.data.messages) || []).reduce((n, m) => n + m.attachments.length, 0));
                    }
                }
            })
            .catch(() => {
                if (emailId === this.props.emailId) {
                    this.setState({loading: false, error: 'Cannot load the email conversation.'});
                }
            });
    }

    // Same flow as the "Read email conversations" switch in the profile: ask for the read permission.
    enableReading() {
        const reconnect = this.state.reconnect;
        this.setState({ permissionDialogOpen: false, enabling: true });
        const turnOn = () => request.post('/api/v1/user/email/read', { enabled: true }, getClientAuthHeader())
            .then((result) => {
                if (result.data.type !== 'AUTHORIZATION') {
                    this.afterEnable();
                    return;
                }

                const popup = window.open(result.data.authUrl, 'popup', 'toolbar=0,status=0,width=500,height=500');
                if (!popup) {
                    request.post('/api/v1/user/email/read', { enabled: false }, getClientAuthHeader()).catch(() => {});
                    toastr.error('Email', 'Please allow popup windows to grant the permission.');
                    this.setState({ enabling: false });
                    return;
                }

                const timer = setInterval(() => {
                    if (popup.closed) {
                        clearInterval(timer);
                        this.afterEnable();
                    }
                }, 1000);
            });

        (reconnect ? request.post('/api/v1/user/email/read', { enabled: false }, getClientAuthHeader()).then(turnOn) : turnOn())
            .catch(() => {
                toastr.error('Email', 'Cannot turn on reading of email conversations.');
                this.setState({ enabling: false });
            });
    }

    afterEnable() {
        this.setState({ enabling: false });
        if (this.props.onEnabled) {
            this.props.onEnabled();
        }

        this.load();
    }

    renderPermission(reconnect) {
        const { sender } = this.props;
        const provider = (this.props.provider || 'email account');
        return (
            <div className="email-conv__notice">
                <p>
                    <strong>{reconnect ? 'Read access needs to be granted again.' : 'Full history is off.'}</strong>{' '}
                    To show replies and attachments, Case Tempo needs read access to the mailbox this email was sent from.
                </p>
                {sender ? (
                    <Button bsStyle="primary"
                            label={reconnect ? 'Grant access again ...' : 'Turn on full history ...'}
                            loading={this.state.enabling}
                            disabled={this.state.enabling}
                            onClick={() => this.setState({ permissionDialogOpen: true, reconnect })} />
                ) : (
                    <p>Only the person who sent this email can turn this on, in their profile.</p>
                )}
                <ConfirmDialog title="Allow Case Tempo to read your email?"
                               cancel={() => this.setState({ permissionDialogOpen: false })}
                               confirm={() => this.enableReading()}
                               confirmLabel="Continue"
                               open={this.state.permissionDialogOpen}>
                    <p>Case Tempo will ask your {provider} for read access, so it can show the replies and attachments of emails you sent from Case Tempo.</p>
                    <ul>
                        <li>It reads only the conversations of emails sent from Case Tempo, nothing else in your mailbox.</li>
                        <li>You can turn it off at any time with "Read email conversations" in your profile.</li>
                        <li>On the next screen your email provider will ask you to approve the permission.</li>
                    </ul>
                </ConfirmDialog>
            </div>
        );
    }

    setBusy(key, value) {
        this.setState(state => ({busy: {...state.busy, [key]: value}}));
    }

    attachmentParams(message, attachment) {
        return {
            messageId: message.id,
            attachmentId: attachment.id,
            fileName: attachment.name,
            size: attachment.size,
        };
    }

    closePreview() {
        if (this.state.preview && this.state.preview.url) {
            window.URL.revokeObjectURL(this.state.preview.url);
        }

        this.setState({ preview: undefined });
    }

    // Opens the preview dialog: see the file, rename it and save it to the case documents (or download it).
    openPreview(message, attachment) {
        const key = `d:${message.id}:${attachment.id}`;
        this.setBusy(key, true);
        request.get(this.url('attachment'), {
            ...getClientAuthHeader(),
            params: this.attachmentParams(message, attachment),
            responseType: 'blob',
        }).then(result => {
            const ext = fileExtension(attachment.name);
            const blob = new Blob([result.data], {type: ext === 'pdf' ? 'application/pdf' : (IMAGE_TYPES[ext] || 'application/octet-stream')});
            const dotted = (attachment.name || '').lastIndexOf('.') > 0;
            this.setState({
                preview: {
                    message,
                    attachment,
                    blob,
                    url: window.URL.createObjectURL(blob),
                    name: dotted ? attachment.name.slice(0, attachment.name.lastIndexOf('.')) : attachment.name,
                },
            });
        }).catch(() => {
            toastr.error('Attachment', 'Cannot load the attachment.');
        }).then(() => this.setBusy(key, false));
    }

    downloadPreview() {
        const { preview } = this.state;
        const link = document.createElement('a');
        link.href = preview.url;
        link.download = preview.attachment.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    renderPreview() {
        const { preview } = this.state;
        if (!preview) {
            return null;
        }

        const { attachment, message } = preview;
        const ext = fileExtension(attachment.name);
        const kind = previewKind(ext);
        const key = `s:${message.id}:${attachment.id}`;
        const finalName = preview.name.trim() ? `${preview.name.trim()}${ext ? `.${ext}` : ''}` : attachment.name;
        return (
            <ConfirmDialog title="Attachment"
                           cancel={() => this.closePreview()}
                           open
                           size="welcome"
                           zIndex={1100}>
                <Row>
                    <Col xs={12} md={4} style={{ marginBottom: 20 }}>
                        <label htmlFor="email-attachment-name">Save as</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                            <input id="email-attachment-name"
                                   className="form-control"
                                   value={preview.name}
                                   disabled={!!this.state.saved[key]}
                                   onChange={evt => this.setState({ preview: { ...preview, name: evt.target.value } })} />
                            {ext && <span>.{ext}</span>}
                        </div>
                        <div className="email-conv__file-size" style={{ marginBottom: 12 }}>{formatSize(attachment.size)}</div>
                        {this.state.saved[key] ? (
                            <div className="email-conv__saved" style={{ marginBottom: 12 }}>&#10003; Saved in documents</div>
                        ) : (
                            <Button label="Save to documents"
                                    icon="floppy-disk"
                                    bsStyle="primary"
                                    loading={!!this.state.busy[key]}
                                    onClick={() => this.save(message, attachment, false, finalName)} />
                        )}
                        {' '}
                        <Button label="Download" icon="download-alt" onClick={() => this.downloadPreview()} />
                    </Col>
                    <Col xs={12} md={8}>
                        {kind === 'pdf' && (<iframe title="Attachment preview" src={preview.url} style={{ width: '100%', height: '65vh', border: '1px solid #dde1e8' }} />)}
                        {kind === 'image' && (<img alt={attachment.name} src={preview.url} style={{ maxWidth: '100%', maxHeight: '65vh', display: 'block' }} />)}
                        {!kind && (<div className="email-conv__notice">Preview is not available for this file type. Save it to the documents or download it.</div>)}
                    </Col>
                </Row>
            </ConfirmDialog>
        );
    }

    download(message, attachment) {
        const key = `d:${message.id}:${attachment.id}`;
        this.setBusy(key, true);
        request.get(this.url('attachment'), {
            ...getClientAuthHeader(),
            params: this.attachmentParams(message, attachment),
            responseType: 'blob',
        }).then(result => {
            const href = window.URL.createObjectURL(result.data);
            const link = document.createElement('a');
            link.href = href;
            link.download = attachment.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(href), 10000);
        }).catch(() => {
            toastr.error('Attachment', 'Cannot download the attachment.');
        }).then(() => this.setBusy(key, false));
    }

    // Resolves to true when saved. `quiet` skips the per-file message (used by "Save all").
    save(message, attachment, quiet, name) {
        const key = `s:${message.id}:${attachment.id}`;
        this.setBusy(key, true);
        return request.post(this.url('attachment/save'), {
            ...this.attachmentParams(message, attachment),
            name,
        }, getClientAuthHeader()).then(result => {
            this.setState(state => ({saved: {...state.saved, [key]: true}}));
            if (!quiet) {
                toastr.success('Documents', `${result.data.name} saved to the case documents.`);
            }

            if (this.props.onAttachmentSaved) {
                this.props.onAttachmentSaved(result.data.name);
            }

            return true;
        }).catch(() => {
            toastr.error('Documents', `Cannot save ${attachment.name} to the case documents.`);
            return false;
        }).then(ok => {
            this.setBusy(key, false);
            return ok;
        });
    }

    // Called by the parent dialog ("Save all attachments" next to "Open in Webmail"): all attachments preselected, renameable.
    openSaveAll() {
        const { data } = this.state;
        if (!data || !data.messages) {
            return;
        }

        const items = [];
        [...data.messages].reverse().forEach(m => m.attachments.forEach(a => {
            const dotted = (a.name || '').lastIndexOf('.') > 0;
            items.push({
                key: `s:${m.id}:${a.id}`,
                message: m,
                attachment: a,
                selected: true,
                name: dotted ? a.name.slice(0, a.name.lastIndexOf('.')) : a.name,
            });
        }));

        this.setState({ saveAllItems: items });
    }

    async confirmSaveAll() {
        const items = this.state.saveAllItems.filter(i => i.selected && !this.state.saved[i.key]);
        this.setState({ savingAll: true });
        let count = 0;
        for (let i = 0; i < items.length; i++) {
            const { message, attachment, name } = items[i];
            const ext = fileExtension(attachment.name);
            const finalName = name.trim() ? `${name.trim()}${ext ? `.${ext}` : ''}` : attachment.name;
            if (await this.save(message, attachment, true, finalName)) {
                count++;
            }
        }

        this.setState({ savingAll: false, saveAllItems: undefined });
        if (count > 0) {
            toastr.success('Documents', `${count} attachment${count > 1 ? 's' : ''} saved to the case documents.`);
        }
    }

    renderSaveAll() {
        const items = this.state.saveAllItems;
        if (!items) {
            return null;
        }

        const chosen = items.filter(i => i.selected && !this.state.saved[i.key]).length;
        const update = (index, change) => this.setState({ saveAllItems: items.map((i, n) => (n === index ? { ...i, ...change } : i)) });
        return (
            <ConfirmDialog title="Save attachments to documents"
                           cancel={() => !this.state.savingAll && this.setState({ saveAllItems: undefined })}
                           open
                           size="large"
                           zIndex={1100}
                           confirmButton={(
                               <Button label={chosen > 0 ? `Save ${chosen} attachment${chosen > 1 ? 's' : ''}` : 'Nothing to save'}
                                       icon="floppy-disk"
                                       bsStyle="primary"
                                       loading={this.state.savingAll}
                                       disabled={chosen === 0 || this.state.savingAll}
                                       onClick={() => this.confirmSaveAll()} />
                           )}>
                <p>The files are saved to the documents of this case. You can rename them first.</p>
                {items.map((item, index) => {
                    const ext = fileExtension(item.attachment.name);
                    const saved = !!this.state.saved[item.key];
                    return (
                        <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                            <input type="checkbox"
                                   checked={item.selected && !saved}
                                   disabled={saved || this.state.savingAll}
                                   onChange={evt => update(index, { selected: evt.target.checked })} />
                            <input className="form-control"
                                   style={{ flex: 1 }}
                                   value={item.name}
                                   disabled={saved || !item.selected || this.state.savingAll}
                                   onChange={evt => update(index, { name: evt.target.value })} />
                            <span style={{ minWidth: 48 }}>{ext ? `.${ext}` : ''}</span>
                            <span className="email-conv__file-size" style={{ minWidth: 70, textAlign: 'right' }}>{saved ? '\u2713 Saved' : formatSize(item.attachment.size)}</span>
                        </div>
                    );
                })}
            </ConfirmDialog>
        );
    }

    renderFile(message, a) {
        const key = `s:${message.id}:${a.id}`;
        const ext = fileExtension(a.name);
        return (
            <div key={a.id} className="email-conv__file">
                <span className={`email-conv__badge email-conv__badge--${badgeKind(ext)}`}>{ext.slice(0, 4) || 'file'}</span>
                <span className="email-conv__file-info">
                    <div className="email-conv__file-name" title={a.name}>{a.name}</div>
                    <div className="email-conv__file-size">{formatSize(a.size)}</div>
                </span>
                <span className="email-conv__file-actions">
                    <Button label="View / Download"
                            icon="download-alt"
                            bsSize="xsmall"
                            loading={!!this.state.busy[`d:${message.id}:${a.id}`]}
                            onClick={() => this.openPreview(message, a)} />
                    {this.state.saved[key] ? (
                        <span className="email-conv__saved">&#10003; Saved in documents</span>
                    ) : (
                        <Button label="Save to documents"
                                icon="floppy-disk"
                                bsSize="xsmall"
                                bsStyle="primary"
                                loading={!!this.state.busy[key]}
                                onClick={() => this.save(message, a)} />
                    )}
                </span>
            </div>
        );
    }

    renderMessage(message) {
        const sent = isSender(message.from, this.props.sender);
        return (
            <article key={message.id} className={`email-conv__msg${sent ? ' email-conv__msg--sent' : ''}`}>
                <div className="email-conv__msg-head">
                    <span className="email-conv__avatar">{initials(message.from)}</span>
                    <span className="email-conv__who">
                        <strong>{displayName(message.from)}</strong>
                        <div className="email-conv__to">to {message.to}{message.cc ? ` | Cc: ${message.cc}` : ''}</div>
                    </span>
                    <span className="email-conv__when">{message.date && new Date(message.date).toLocaleString()}</span>
                </div>
                <iframe title={`email-${message.id}`}
                        className="email-conv__frame"
                        sandbox="allow-same-origin"
                        srcDoc={bodyDocument(this.state.showQuoted[message.id] && message.quotedHtml ? `${message.bodyHtml}<hr>${message.quotedHtml}` : message.bodyHtml)}
                        style={{height: 120}}
                        onLoad={evt => {
                            try {
                                const doc = evt.target.contentDocument;
                                evt.target.style.height = `${Math.min(doc.documentElement.scrollHeight + 8, 700)}px`;
                            } catch (e) {}
                        }} />
                {message.quotedHtml && (
                    <div style={{ margin: '0 14px 8px 58px' }}>
                        <button type="button"
                                className="btn btn-link btn-xs"
                                title={this.state.showQuoted[message.id] ? 'Hide the earlier messages' : 'Show the earlier messages quoted in this reply'}
                                onClick={() => this.setState(state => ({ showQuoted: { ...state.showQuoted, [message.id]: !state.showQuoted[message.id] } }))}>
                            {this.state.showQuoted[message.id] ? 'Hide quoted text' : '\u2026 Show quoted text'}
                        </button>
                    </div>)}
                {message.attachments.length > 0 && (
                    <div className="email-conv__files">
                        {message.attachments.map(a => this.renderFile(message, a))}
                    </div>)}
            </article>
        );
    }

    render() {
        const { loading, data, error } = this.state;
        if (loading) {
            return (<div className="email-conv__notice">Loading conversation ...</div>);
        }

        if (error) {
            return (<div className="email-conv__notice">{error}{this.props.fallback}</div>);
        }

        if (!data.enabled) {
            return this.renderPermission(false);
        }

        if (data.accountChanged) {
            return (
                <div className="email-conv__notice">
                    <p>This email was sent from <strong>{data.fromAddress}</strong>, but the connected email account is now <strong>{data.currentAddress}</strong>.</p>
                    <p>To read this conversation, connect {data.fromAddress} again.</p>
                </div>);
        }

        if (data.needsReconnect) {
            return this.renderPermission(true);
        }

        if (data.notFound || data.messages.length === 0) {
            return (
                <div className="email-conv__notice">
                    <p>This conversation was not found in the mailbox of the sender.</p>
                    {this.props.fallback}
                </div>);
        }

        // Newest first, everything visible by scrolling.
        const messages = [...data.messages].reverse();
        const attachmentCount = messages.reduce((n, m) => n + m.attachments.length, 0);
        return (
            <React.Fragment>
                {this.renderPreview()}
                <div className="email-conv__head">
                    <span className="email-conv__pill">{data.account}</span>
                    <span>{messages.length} message{messages.length > 1 ? 's' : ''}, newest first</span>
                    {attachmentCount > 0 && <span>&middot; {attachmentCount} attachment{attachmentCount > 1 ? 's' : ''}</span>}
                </div>
                <div className="email-conv__thread">
                    {messages.map(m => this.renderMessage(m))}
                </div>
                <div className="email-conv__foot">
                    <span>Replies appear here after the client answers in the sender's mailbox.</span>
                </div>
                {this.renderSaveAll()}
            </React.Fragment>
        );
    }
}

EmailConversation.propTypes = {
    caseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    emailId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    fallback: PropTypes.node,
    sender: PropTypes.string,
    onAttachmentSaved: PropTypes.func,
    onAttachmentCount: PropTypes.func,
};

export default EmailConversation;
